import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';
import { useJitsu } from '@jitsu/jitsu-react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { Form, FormControl, FormField } from '@tg-web/components';
import { useCallbackRef } from '@tg-web/utils';

import {
  OjGoogleAccount,
  useActivateGoogleAccountV2,
} from '../../../shared/api';
import { useMainButton } from '../../../shared/lib/useMainButton';
import { GoogleAnalytics } from '../../../shared/model/analytics';
import {
  Island,
  IslandCheckboxCell,
  IslandSectionHeader,
} from '../../../shared/ui/island';

const FormSchema = z.object({
  items: z.array(z.string()),
});

export type IslandCalendarsSynchronizationFormProps = {
  googleAccount: OjGoogleAccount;
  /** True while any of the page's three modals is open. */
  isModalOpen: boolean;
  onSuccess?: VoidFunction;
};

/**
 * The island counterpart of `CalendarsSynchronizationForm`. The selection, what
 * submit sends, the analytics event and the cache invalidation are unchanged;
 * Save moves from a button in the page onto Telegram's MainButton, wired the
 * way the Appearance form wires it.
 */
export function IslandCalendarsSynchronizationForm({
  googleAccount,
  isModalOpen,
  onSuccess,
}: IslandCalendarsSynchronizationFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { analytics } = useJitsu();

  const form = useForm<z.infer<typeof FormSchema>>({
    defaultValues: {
      items: googleAccount.google_calendars
        ?.filter((it) => it.active)
        .map((it) => it.id),
    },
    resolver: zodResolver(FormSchema),
  });

  const googleAccountCalendars = useActivateGoogleAccountV2({
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ['events'],
      });
      queryClient.invalidateQueries({
        queryKey: ['v2', 'google_accounts'],
      });

      onSuccess?.();
    },
  });

  const onSubmitCallbackRef = useCallbackRef(
    (data: z.infer<typeof FormSchema>) => {
      analytics.track(GoogleAnalytics.CALENDARS_SYNC_SUBMIT, {
        selectedCount: data.items.length,
        totalCount: googleAccount.google_calendars?.length ?? 0,
      });
      googleAccountCalendars.mutate({
        body: { google_account: { calendar_ids: data.items } },
        pathParams: { id: googleAccount.id },
      });
    },
  );

  const handleSubmit = useMemo(
    () => form.handleSubmit((values) => onSubmitCallbackRef(values)),
    [form, onSubmitCallbackRef],
  );

  /*
   * `isHidden` is the event form's isSomeModalShown gate, which the Appearance
   * screen did not need and this one does: three modals can open over it, and
   * Telegram's button would otherwise float on top of them.
   *
   * The double-submit guard is Telegram's -- MainButton.showProgress() leaves
   * the button inactive unless called with leaveActive, and useMainButton calls
   * it bare. Teardown is useMainButton's own cleanup on unmount.
   */
  useMainButton({
    isHidden: isModalOpen,
    isLoading: googleAccountCalendars.isPending,
    onClick: handleSubmit,
    text: t('all:common.buttons.save'),
  });

  return (
    <Form {...form}>
      {/*
       * mt-3 for the same reason as the Appearance form: this is a nested
       * column, so its first section header is a :first-child and drops the
       * 12px it would otherwise add above itself.
       */}
      <form className="mt-3 flex flex-col gap-3" onSubmit={handleSubmit}>
        <IslandSectionHeader>
          {t('all:google.calendar_sync.import.title')}
        </IslandSectionHeader>
        <FormField
          render={() => (
            <Island>
              {googleAccount.google_calendars?.map((it) => (
                <FormField
                  render={({ field }) => (
                    <FormControl>
                      <IslandCheckboxCell
                        onCheckedChange={(checked) =>
                          checked
                            ? field.onChange([...field.value, it.id])
                            : field.onChange(
                                field.value?.filter((value) => value !== it.id),
                              )
                        }
                        checked={field.value?.includes(it.id)}
                        disabled={googleAccountCalendars.isPending}
                        id={it.id}
                      >
                        {it.name}
                      </IslandCheckboxCell>
                    </FormControl>
                  )}
                  control={form.control}
                  key={it.id}
                  name="items"
                />
              ))}
            </Island>
          )}
          control={form.control}
          name="items"
        />
      </form>
    </Form>
  );
}
